import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CreditCard,
  FileText,
  HandCoins,
  Home,
  Landmark,
  Mail,
  Phone,
  ShieldCheck,
  UserRound,
  Users,
} from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { PageLoading } from "@/components/page-loading";
import { PageShell } from "@/components/page-shell";
import { describeAddress } from "@/components/records/address";
import { describeEmployment } from "@/components/records/employment";
import { Section } from "@/components/section";
import { describeAdditionalIncome } from "@/components/task-forms/additional-income";
import {
  additionalIncomesQueryOptions,
  addressesQueryOptions,
  documentsQueryOptions,
  documentTypesQueryOptions,
  employmentsQueryOptions,
  ensureConsumerQueryData,
  formOptionsQueryOptions,
  identificationCardsQueryOptions,
  plaidLinksQueryOptions,
  profileQueryOptions,
  referencesQueryOptions,
} from "@/lib/data";
import {
  formatLongDate,
  formatShortDate,
  formatSsnLast4,
  startCase,
} from "@/lib/formatters";
import { requireSession } from "@/lib/session";
import { labelFor } from "@/lib/utils";

export const Route = createFileRoute("/profile")({
  component: ProfilePage,
  beforeLoad: requireSession,
  loader: () =>
    Promise.all([
      ensureConsumerQueryData(profileQueryOptions),
      ensureConsumerQueryData(addressesQueryOptions),
      ensureConsumerQueryData(employmentsQueryOptions),
      ensureConsumerQueryData(additionalIncomesQueryOptions),
      ensureConsumerQueryData(referencesQueryOptions),
      ensureConsumerQueryData(identificationCardsQueryOptions),
      ensureConsumerQueryData(documentsQueryOptions),
      ensureConsumerQueryData(documentTypesQueryOptions),
      ensureConsumerQueryData(plaidLinksQueryOptions),
    ]),
});

function ProfilePage() {
  const navigate = useNavigate();
  const { data: profile, isLoading: profileLoading } =
    useQuery(profileQueryOptions);
  const { data: addressesData, isLoading: addressesLoading } = useQuery(
    addressesQueryOptions,
  );
  const { data: employmentsData, isLoading: employmentsLoading } = useQuery(
    employmentsQueryOptions,
  );
  const { data: incomesData, isLoading: incomesLoading } = useQuery(
    additionalIncomesQueryOptions,
  );
  const { data: referencesData, isLoading: referencesLoading } = useQuery(
    referencesQueryOptions,
  );
  const { data: cardsData, isLoading: cardsLoading } = useQuery(
    identificationCardsQueryOptions,
  );
  const { data: documentsData, isLoading: documentsLoading } = useQuery(
    documentsQueryOptions,
  );
  const { data: documentTypes } = useQuery(documentTypesQueryOptions);
  const { data: plaidLinksData, isLoading: plaidLinksLoading } = useQuery(
    plaidLinksQueryOptions,
  );
  const { data: formOptions } = useQuery(formOptionsQueryOptions);

  const isLoading =
    profileLoading ||
    addressesLoading ||
    employmentsLoading ||
    incomesLoading ||
    referencesLoading ||
    cardsLoading ||
    documentsLoading ||
    plaidLinksLoading;

  if (isLoading || !profile) {
    return <PageLoading />;
  }

  const formattedDob = profile.date_of_birth
    ? formatLongDate(profile.date_of_birth)
    : null;
  const addresses = addressesData?.addresses ?? [];
  const employments = employmentsData?.employments ?? [];
  const incomes = incomesData?.additional_incomes ?? [];
  const references = referencesData?.references ?? [];
  const cards = cardsData?.identification_cards ?? [];
  const documents = documentsData?.documents ?? [];
  const plaidLinks = plaidLinksData?.plaid_links ?? [];

  return (
    <PageShell>
      <PageHeader
        title="Your profile"
        description="Review everything you've shared with the dealer."
      />

      <div className="space-y-5">
        {/* Contact and identity are edited separately, matching the two tasks a dealer can ask
            for independently — so each section gets its own way in. */}
        <Section title="Contact">
          <dl className="divide-y divide-border/70">
            <ProfileRow
              icon={<UserRound className="size-4" />}
              label="Full name"
              value={profile.full_name}
            />
            <ProfileRow
              icon={<Phone className="size-4" />}
              label="Phone number"
              value={profile.phone_number}
              tabular
            />
            <ProfileRow
              icon={<Mail className="size-4" />}
              label="Email"
              value={profile.email}
            />
          </dl>
        </Section>

        <div className="-mt-3 flex justify-end">
          <ActionLink onClick={() => navigate({ to: "/profile/contact" })}>
            Edit contact information →
          </ActionLink>
        </div>

        <Section title="Identity">
          <dl className="divide-y divide-border/70">
            <ProfileRow label="Date of birth" value={formattedDob} tabular />
            <ProfileRow
              icon={<ShieldCheck className="size-4" />}
              label="SSN"
              value={formatSsnLast4(profile.social_security_number)}
              tabular
            />
            {formOptions?.require_credit_consent && (
              <ProfileRow
                label="Credit check consent"
                value={profile.consents_to_credit_check ? "Yes" : "No"}
              />
            )}
          </dl>
        </Section>

        <div className="-mt-3 flex justify-end">
          <ActionLink onClick={() => navigate({ to: "/profile/identity" })}>
            Edit identity information →
          </ActionLink>
        </div>

        <Section title="Government ID">
          <SummaryGroup emptyMessage="No ID uploaded yet.">
            {cards.map((card) => (
              <SummaryItem
                key={card.id}
                icon={<CreditCard className="size-4" />}
                title={labelFor(
                  formOptions?.id_types,
                  card.identification_card_type,
                  card.identification_card_type,
                )}
                detail={`Issued by ${card.issuer}`}
                meta={card.completed ? "Complete" : "Back image needed"}
              />
            ))}
          </SummaryGroup>
        </Section>

        <Section title="Bank statements">
          <SummaryGroup emptyMessage="No bank connection shared yet.">
            {plaidLinks.map((link) => (
              <SummaryItem
                key={link.id}
                icon={<Landmark className="size-4" />}
                title={link.institution_name || "Bank connection"}
                detail={`Connected ${formatLongDate(link.created_at)}`}
              />
            ))}
          </SummaryGroup>
        </Section>

        <Section
          title="Documents"
          description={
            <ActionLink onClick={() => navigate({ to: "/documents" })}>
              Manage
            </ActionLink>
          }
        >
          <SummaryGroup emptyMessage="No supporting documents uploaded yet.">
            {documents.map((document) => {
              // Category comes from the document's type; the API's own
              // document_category field is a dealer-side enum that is never
              // set for consumer uploads.
              const category = documentTypes?.find(
                (t) => t.id === document.uploaded_document_type_id,
              )?.category;
              return (
                <SummaryItem
                  key={document.id}
                  icon={<FileText className="size-4" />}
                  title={document.document_type_name}
                  detail={labelFor(
                    formOptions?.document_categories,
                    category,
                    category ? startCase(category) : "Other",
                  )}
                  meta={formatShortDate(document.created_at)}
                />
              );
            })}
          </SummaryGroup>
        </Section>

        <Section title="Addresses">
          <SummaryGroup emptyMessage="No address history added yet.">
            {addresses.map((address) => {
              const { street, locality, dates } = describeAddress(address, {
                housingStatuses: formOptions?.housing_statuses,
              });
              return (
                <SummaryItem
                  key={address.id}
                  icon={<Home className="size-4" />}
                  title={`${address.label} address`}
                  detail={`${street}, ${locality}`}
                  meta={dates}
                />
              );
            })}
          </SummaryGroup>
        </Section>

        <Section title="Employment">
          <SummaryGroup emptyMessage="No employment history added yet.">
            {employments.map((employment) => {
              const { role, employer, dates, pay, workplace } =
                describeEmployment(employment);
              return (
                <SummaryItem
                  key={employment.id}
                  icon={<Landmark className="size-4" />}
                  title={`${role} at ${employer}`}
                  detail={workplace}
                  meta={[dates, pay].filter(Boolean).join(" • ")}
                />
              );
            })}
          </SummaryGroup>
        </Section>

        <Section title="Additional income">
          <SummaryGroup emptyMessage="No additional income added yet.">
            {incomes.map((income) => {
              const { source, pay } = describeAdditionalIncome(
                income,
                formOptions?.additional_income_types,
              );
              return (
                <SummaryItem
                  key={income.id}
                  icon={<HandCoins className="size-4" />}
                  title={source}
                  detail={pay}
                />
              );
            })}
          </SummaryGroup>
        </Section>

        <Section title="References">
          <SummaryGroup emptyMessage="No references shared yet.">
            {references.map((reference) => (
              <SummaryItem
                key={reference.id}
                icon={<Users className="size-4" />}
                title={reference.full_name}
                detail={[
                  reference.relationship,
                  reference.city,
                  reference.region,
                ]
                  .filter(Boolean)
                  .join(" • ")}
                meta={reference.original_phone_number}
              />
            ))}
          </SummaryGroup>
        </Section>
      </div>
    </PageShell>
  );
}

function ActionLink({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-primary hover:underline"
    >
      {children}
    </button>
  );
}

function SummaryGroup({
  children,
  emptyMessage,
}: {
  children: React.ReactNode;
  emptyMessage: string;
}) {
  const items = Array.isArray(children) ? children.filter(Boolean) : [children];

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return <div className="space-y-3">{items}</div>;
}

function SummaryItem({
  icon,
  title,
  detail,
  meta,
}: {
  icon: React.ReactNode;
  title: string;
  detail?: string | null;
  meta?: string | null;
}) {
  return (
    <div className="flex gap-3 rounded-lg border border-border/70 bg-background/60 p-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-foreground">{title}</p>
        {detail && <p className="text-sm text-muted-foreground">{detail}</p>}
        {meta && <p className="text-xs text-muted-foreground/80">{meta}</p>}
      </div>
    </div>
  );
}

function ProfileRow({
  icon,
  label,
  value,
  tabular,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string | null | undefined;
  tabular?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <dt className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon ? <span className="text-muted-foreground/70">{icon}</span> : null}
        {label}
      </dt>
      <dd
        className={`truncate text-right text-sm font-medium ${
          value ? "text-foreground" : "text-muted-foreground/70"
        } ${tabular ? "tabular-nums" : ""}`}
      >
        {value || "Not provided"}
      </dd>
    </div>
  );
}
